import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "../components/ui/input";
import { AlertSettingsDialog } from "../components/AlertSettingsDialog";
import { WorkshopInfoDialog } from "../components/WorkshopInfoDialog";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { ShelfCard } from "../components/ShelfCard";
import { CarFormDialog } from "../components/CarFormDialog";
import { ShelfDetailDialog } from "../components/ShelfDetailDialog";
import { VoucherDialog } from "../components/VoucherDialog";
import { AddShelvesDialog } from "../components/AddShelvesDialog";
import { CapacityAlert } from "../components/CapacityAlert";
import { LabelDialog } from "../components/LabelDialog";

const Stat = ({ label, value, cls, id }) => (
  <div className={`rounded-lg border-2 p-4 ${cls}`}>
    <div className="text-xs font-bold opacity-70">{label}</div>
    <div className="text-3xl font-black mt-1" data-testid={id}>{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [shelves, setShelves] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ open: false, shelf: null, car: null });
  const [detail, setDetail] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [label, setLabel] = useState(null);
  const [plateQ, setPlateQ] = useState("");
  const [threshold, setThreshold] = useState(80);

  const load = useCallback(() => api.get("/shelves").then((r) => setShelves(r.data)), []);
  useEffect(() => { load(); api.get("/settings").then((r) => setThreshold(r.data.alert_threshold)); }, [load]);

  const norm = (s) => s.replace(/\s+/g, "").toLowerCase();
  const matches = useMemo(() => {
    const q = norm(plateQ);
    return q ? new Set(shelves.filter((s) => s.car && (norm(s.car.plate_number).includes(q) || (s.car.parts || []).some((p) => !p.delivered && p.part_number && norm(p.part_number).includes(q)))).map((s) => s.number)) : new Set();
  }, [plateQ, shelves]);
  useEffect(() => {
    const first = [...matches][0];
    if (first) document.getElementById(`shelf-${first}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [matches]);

  const occupied = shelves.filter((s) => s.car).length;
  const shown = shelves.filter((s) => filter === "all" || (filter === "occupied" ? s.car : !s.car));
  const openShelf = (s) => (s.car ? setDetail(s.car) : setForm({ open: true, shelf: s.number, car: null }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold">{t("shelves")}</h1>
        </div>
        {user.role === "admin" && (
          <div className="flex flex-wrap gap-2">
            <WorkshopInfoDialog />
            <AlertSettingsDialog threshold={threshold} onSaved={(d) => setThreshold(d.alert_threshold)} />
            <AddShelvesDialog onDone={load} />
          </div>
        )}
      </div>
      <CapacityAlert total={shelves.length} occupied={occupied} threshold={threshold} />
      <div className="grid grid-cols-3 gap-3 md:gap-6 max-w-3xl">
        <Stat label={t("total")} value={shelves.length} cls="bg-white border-slate-200" id="stat-total" />
        <Stat label={t("available")} value={shelves.length - occupied} cls="bg-emerald-50 border-emerald-300 text-emerald-950" id="stat-available" />
        <Stat label={t("occupied")} value={occupied} cls="bg-rose-50 border-rose-300 text-rose-950" id="stat-occupied" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {["all", "available", "occupied"].map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold border transition-colors ${filter === f ? "bg-[#3E3E40] text-white border-[#3E3E40]" : "bg-white text-slate-700 hover:bg-slate-100"}`}>{t(f)}</button>
          ))}
        </div>
        <div className="relative w-full sm:w-72 ms-auto">
          <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-slate-400" />
          <Input data-testid="plate-search-input" className="bg-white ps-9 pe-9 font-mono" placeholder={t("plateSearch")} value={plateQ} onChange={(e) => setPlateQ(e.target.value)} />
          {plateQ && <button data-testid="plate-search-clear" onClick={() => setPlateQ("")} className="absolute top-1/2 -translate-y-1/2 end-3 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
        </div>
        {plateQ && (
          <span data-testid="plate-search-result" className={`text-sm font-bold ${matches.size ? "text-sky-700" : "text-rose-600"}`}>
            {matches.size ? `${t("found")}: ${t("shelf")} ${[...matches].join("، ")}` : t("notFound")}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-10 gap-3 md:gap-4" data-testid="shelves-grid">
        {shown.map((s, i) => <ShelfCard key={s.number} shelf={s} index={i} highlight={matches.has(s.number)} onClick={() => openShelf(s)} />)}
      </div>
      <CarFormDialog open={form.open} shelfNumber={form.shelf} car={form.car} onOpenChange={(o) => setForm((f) => ({ ...f, open: o }))} onDone={load} />
      <ShelfDetailDialog car={detail} onOpenChange={(o) => !o && setDetail(null)}
        onEdit={(c) => { setDetail(null); setForm({ open: true, shelf: c.shelf_number, car: c }); }}
        onDispatched={(c) => { load(); setVoucher({ car: c, voucher: c.voucher }); }}
        onLabel={(c) => { setDetail(null); setLabel(c); }} />
      <LabelDialog car={label} onOpenChange={(o) => !o && setLabel(null)} />
      <VoucherDialog data={voucher} onOpenChange={(o) => !o && setVoucher(null)} />
    </div>
  );
}
